import Alpine from 'alpinejs';
import { renderQrById } from '../../helpers/renderQr.js';

const PHOTO_MAX_BYTES = 1024 * 1024;
const PHOTO_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

function validatePhotoFile(file) {
  if (!file) return 'Pilih file foto terlebih dahulu.';
  if (!PHOTO_TYPES.has(String(file.type || '').toLowerCase())) {
    return 'Foto harus berformat JPG, JPEG, atau PNG.';
  }
  if (file.size > PHOTO_MAX_BYTES) {
    return 'Ukuran foto maksimal 1 MB.';
  }
  return null;
}

function createInvBuilder(cfg) {
  return {
    loggedIn: false,
    needTemplate: false,
    path: window.location.pathname + window.location.search,
    manageUrl: cfg.manageUrl || '/invitations/manage',
    id: cfg.id || null,
    slug: null,
    status: 'draft',
    template: null,
    saving: false,
    uploading: null,
    uploadingMusic: false,
    shareGuestName: '',
    shareData: null,
    shareLoading: false,
    musicFileUrl: null,
    fieldErrors: {},
    photos: { groom: null, bride: null, couple: null, qris: null },
    galleryPhotos: [],
    formSections: [],
    form: {
      template_id: null,
      groom_name: '', groom_nickname: '', groom_father: '', groom_mother: '', groom_child_order: '',
      bride_name: '', bride_nickname: '', bride_father: '', bride_mother: '', bride_child_order: '',
      bride_first: false,
      event_description: '',
      akad_date: '', akad_time: '', akad_venue_name: '', akad_venue_address: '',
      resepsi_same_as_akad: false,
      event_date: '', event_time: '',
      venue_name: '', venue_address: '', venue_lat: '', venue_lng: '',
      music_url: '',
      also_inviting: [],
      love_story: [], bank_accounts: [],
      theme: 'auto',
    },

    get displayNames() {
      const g = this.form.groom_nickname || this.form.groom_name || 'Pria';
      const b = this.form.bride_nickname || this.form.bride_name || 'Wanita';
      return this.form.bride_first ? `${b} & ${g}` : `${g} & ${b}`;
    },

    hasSection(key) {
      if (!this.formSections.length) return true;
      return this.formSections.includes(key);
    },

    async init() {
      this.path = window.location.pathname + window.location.search;
      this.loggedIn = !!Alpine.store('auth')?.isLoggedIn;
      if (!this.loggedIn) {
        window.location.href = '/login?redirect=' + encodeURIComponent(this.path);
        return;
      }
      if (this.id) await this.loadInvitation();
      else if (cfg.templateSlug) await this.loadTemplate(cfg.templateSlug);
      else this.needTemplate = true;
    },

    async loadTemplate(slug) {
      try {
        const { data } = await window.axios.get(`/api/v1/invitations/templates/${slug}`);
        this.template = data.data;
        this.form.template_id = this.template.id;
        this.formSections = this.template.form_schema || [];
      } catch (_) {
        this.needTemplate = true;
      }
    },

    async loadInvitation() {
      try {
        const { data } = await window.axios.get(`/api/v1/invitations/${this.id}`);
        this.applyInvitation(data.data);
      } catch (_) {
        window.toast?.('Undangan tidak ditemukan', 'error');
        setTimeout(() => { window.location.href = this.manageUrl; }, 800);
      }
    },

    applyInvitation(inv) {
      this.id = inv.id;
      this.slug = inv.slug;
      this.status = inv.status;
      this.template = inv.template || this.template;
      this.formSections = inv.template?.form_schema || this.formSections;
      this.form.template_id = inv.template?.id || this.form.template_id;
      this.form.groom_name = inv.groom_name || '';
      this.form.groom_nickname = inv.groom_nickname || '';
      this.form.groom_father = inv.groom_father || '';
      this.form.groom_mother = inv.groom_mother || '';
      this.form.groom_child_order = inv.groom_child_order || '';
      this.form.bride_name = inv.bride_name || '';
      this.form.bride_nickname = inv.bride_nickname || '';
      this.form.bride_father = inv.bride_father || '';
      this.form.bride_mother = inv.bride_mother || '';
      this.form.bride_child_order = inv.bride_child_order || '';
      this.form.bride_first = !!inv.bride_first;
      this.form.event_description = inv.event_description || '';
      this.form.akad_date = inv.akad?.date ? String(inv.akad.date).slice(0, 10) : '';
      this.form.akad_time = inv.akad?.time || '';
      this.form.akad_venue_name = inv.akad?.name || '';
      this.form.akad_venue_address = inv.akad?.address || '';
      this.form.resepsi_same_as_akad = !!inv.resepsi_same_as_akad;
      this.form.event_date = inv.event_date ? String(inv.event_date).slice(0, 10) : '';
      this.form.event_time = inv.event_time || '';
      this.form.venue_name = inv.venue?.name || '';
      this.form.venue_address = inv.venue?.address || '';
      this.form.venue_lat = inv.venue?.lat ?? '';
      this.form.venue_lng = inv.venue?.lng ?? '';
      this.form.music_url = inv.music_url || '';
      this.musicFileUrl = inv.music_file_url || null;
      this.form.also_inviting = Array.isArray(inv.also_inviting)
        ? inv.also_inviting.map(p => ({ name: p.name || '', role: p.role || '' }))
        : [];
      this.form.love_story = Array.isArray(inv.love_story)
        ? inv.love_story.map(s => ({ year: s.year || s.title || '', story: s.story || s.text || '' }))
        : [];
      this.form.bank_accounts = Array.isArray(inv.bank_accounts)
        ? inv.bank_accounts.map(b => ({ bank: b.bank || '', number: b.number || '', holder: b.holder || '' }))
        : [];
      this.form.theme = inv.theme || 'light';
      this.photos = {
        groom: inv.photos?.groom || null,
        bride: inv.photos?.bride || null,
        couple: inv.photos?.couple || null,
        qris: inv.qris_image || null,
      };
      this.galleryPhotos = Array.isArray(inv.gallery) ? [...inv.gallery] : [];
    },

    syncResepsiFromAkad() {
      if (!this.form.resepsi_same_as_akad) return;
      this.form.event_date = this.form.akad_date;
      this.form.event_time = this.form.akad_time;
      if (this.form.akad_venue_name) this.form.venue_name = this.form.akad_venue_name;
      if (this.form.akad_venue_address) this.form.venue_address = this.form.akad_venue_address;
    },

    fieldError(key) {
      const e = this.fieldErrors[key];
      return Array.isArray(e) ? e[0] : (e || '');
    },

    clearError(key) {
      if (this.fieldErrors[key]) {
        const next = { ...this.fieldErrors };
        delete next[key];
        this.fieldErrors = next;
      }
    },

    setValidationErrors(errs) {
      this.fieldErrors = errs && typeof errs === 'object' ? errs : {};
      const first = Object.values(this.fieldErrors)[0];
      const msg = Array.isArray(first) ? first[0] : null;
      if (msg) window.toast?.(msg, 'error');
    },

    async ensureSaved() {
      if (this.id) return true;
      if (!this.form.template_id) {
        window.toast?.('Pilih template dulu', 'error');
        return false;
      }
      this.saving = true;
      try {
        const { data } = await window.axios.post('/api/v1/invitations', this.payload());
        this.applyInvitation(data.data);
        history.replaceState({}, '', `/invitations/${data.data.id}/edit`);
        return true;
      } catch (e) {
        this.setValidationErrors(e.response?.data?.errors);
        return false;
      } finally {
        this.saving = false;
      }
    },

    payload() {
      const p = { ...this.form };
      if (p.resepsi_same_as_akad) {
        p.event_date = p.akad_date || p.event_date;
        p.event_time = p.akad_time || p.event_time;
        p.venue_name = p.akad_venue_name || p.venue_name;
        p.venue_address = p.akad_venue_address || p.venue_address;
      }
      p.also_inviting = (p.also_inviting || []).filter(x => x.name || x.role);
      p.love_story = (p.love_story || []).filter(s => s.year || s.story);
      p.bank_accounts = (p.bank_accounts || []).filter(b => b.bank || b.number || b.holder);
      if (p.venue_lat === '') p.venue_lat = null;
      if (p.venue_lng === '') p.venue_lng = null;
      if (!p.akad_date) p.akad_date = null;
      if (!p.music_url) p.music_url = null;
      return p;
    },

    shareUrl() {
      if (this.shareData?.share_url) return this.shareData.share_url;
      if (!this.slug) return '';
      let u = window.location.origin + '/undangan/' + this.slug;
      const name = (this.shareGuestName || '').trim();
      if (name) u += '?kpd=' + encodeURIComponent(name);
      return u;
    },

    async renderShareQr() {
      if (!this.shareData?.qr_data) return;
      await this.$nextTick();
      await renderQrById('share-guest-qr', this.shareData.qr_data, { width: 200, margin: 1 });
    },

    async prepareShare() {
      const name = (this.shareGuestName || '').trim();
      if (!name) { window.toast?.('Isi nama tamu dulu', 'error'); return; }
      if (!this.id) { window.toast?.('Simpan undangan dulu', 'error'); return; }
      if (this.status !== 'published') { window.toast?.('Publikasikan undangan dulu', 'error'); return; }
      this.shareLoading = true;
      try {
        const { data } = await window.axios.post(`/api/v1/invitations/${this.id}/share-guest`, { name });
        this.shareData = data.data;
        await this.renderShareQr();
        window.toast?.('Link & QR tamu siap dibagikan', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal membuat link tamu', 'error');
      } finally {
        this.shareLoading = false;
      }
    },

    async copyShare() {
      if (!this.shareData?.share_url && (this.shareGuestName || '').trim()) {
        await this.prepareShare();
      }
      navigator.clipboard?.writeText(this.shareUrl()).then(() => window.toast?.('Link disalin', 'success'));
    },

    async save() {
      if (!this.form.template_id) { window.toast?.('Pilih template dulu', 'error'); return; }
      this.saving = true;
      this.fieldErrors = {};
      try {
        const res = this.id
          ? await window.axios.put(`/api/v1/invitations/${this.id}`, this.payload())
          : await window.axios.post('/api/v1/invitations', this.payload());
        const wasNew = !this.id;
        this.applyInvitation(res.data.data);
        if (wasNew) history.replaceState({}, '', `/invitations/${res.data.data.id}/edit`);
        window.toast?.('Undangan tersimpan', 'success');
      } catch (e) {
        this.setValidationErrors(e.response?.data?.errors);
        if (!e.response?.data?.errors) {
          window.toast?.(e.response?.data?.message || 'Gagal menyimpan', 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    async uploadGalleryPhoto(ev) {
      const files = Array.from(ev.target.files || []);
      if (!files.length) { ev.target.value = ''; return; }

      for (const file of files) {
        const photoErr = validatePhotoFile(file);
        if (photoErr) {
          window.toast?.(photoErr, 'error');
          continue;
        }
        if (!(await this.ensureSaved())) break;
        this.uploading = 'gallery';
        try {
          const fd = new FormData();
          fd.append('photo', file);
          const { data } = await window.axios.post(`/api/v1/invitations/${this.id}/gallery`, fd);
          this.applyInvitation(data.data);
        } catch (e) {
          window.toast?.(e.response?.data?.message || 'Gagal upload galeri', 'error');
        }
      }
      this.uploading = null;
      ev.target.value = '';
    },

    async deleteGalleryPhoto(photoId) {
      if (!this.id || !photoId) return;
      try {
        const { data } = await window.axios.delete(`/api/v1/invitations/${this.id}/gallery/${photoId}`);
        this.applyInvitation(data.data);
        window.toast?.('Foto galeri dihapus', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal hapus', 'error');
      }
    },

    async uploadPhoto(type, ev) {
      const file = ev.target.files?.[0];
      if (!file) { ev.target.value = ''; return; }

      const photoErr = validatePhotoFile(file);
      if (photoErr) {
        this.fieldErrors = { ...this.fieldErrors, photo: [photoErr] };
        window.toast?.(photoErr, 'error');
        ev.target.value = '';
        return;
      }
      this.clearError('photo');

      if (!(await this.ensureSaved())) { ev.target.value = ''; return; }
      this.uploading = type;
      try {
        const fd = new FormData();
        fd.append('type', type);
        fd.append('photo', file);
        const { data } = await window.axios.post(`/api/v1/invitations/${this.id}/photo`, fd);
        this.applyInvitation(data.data);
        window.toast?.('Foto diunggah', 'success');
      } catch (e) {
        this.setValidationErrors(e.response?.data?.errors);
        window.toast?.(e.response?.data?.message || 'Gagal upload', 'error');
      } finally {
        this.uploading = null;
        ev.target.value = '';
      }
    },

    async uploadMusic(ev) {
      const file = ev.target.files?.[0];
      if (!file) { ev.target.value = ''; return; }
      if (!(await this.ensureSaved())) { ev.target.value = ''; return; }
      this.uploadingMusic = true;
      try {
        const fd = new FormData();
        fd.append('music', file);
        const { data } = await window.axios.post(`/api/v1/invitations/${this.id}/music`, fd);
        this.applyInvitation(data.data);
        window.toast?.('Backsound diunggah', 'success');
      } catch (e) {
        this.setValidationErrors(e.response?.data?.errors);
        window.toast?.(e.response?.data?.message || 'Gagal upload musik', 'error');
      } finally {
        this.uploadingMusic = false;
        ev.target.value = '';
      }
    },

    async togglePublish() {
      if (!this.id) return;
      const action = this.status === 'published' ? 'unpublish' : 'publish';
      try {
        const { data } = await window.axios.post(`/api/v1/invitations/${this.id}/${action}`);
        this.status = data.data.status;
        this.slug = data.data.slug;
        window.toast?.(this.status === 'published' ? 'Dipublikasikan' : 'Menjadi draft', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal', 'error');
      }
    },
  };
}

Alpine.data('invBuilder', (cfg = {}) => createInvBuilder(cfg));
